import {
  Button,
  Checkbox,
  Form,
  FormField,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@sandtech-hub-monorepo/ui-kit';
import React, { useState } from 'react';
import { DeferralReason } from '@sandtech-hub-monorepo/ehub-client';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { capitalize } from '@nx/devkit/src/utils/string-utils';
import { ReactComponent as WithdrawIcon } from '../../../assets/Icons/withdraw-icon.svg';
import { WithdrawProgramConfirmation } from './WithdrawProgramConfirmation';
import { ReasonsToEnum, UpdateCourseReasons } from './DeferCourse';
import { ProgramEnrollment } from './SelectProgram';

const withdrawFormSchema = z.object({
  reason: z.enum(UpdateCourseReasons),
  description: z.string(),
  termsAgreed: z.boolean(),
});

export const WithdrawProgram = ({
  onCancel,
  programEnrollment,
}: {
  programEnrollment: ProgramEnrollment;
  onCancel: () => void;
}) => {
  const [selectedState, setSelectedState] = useState<'initial' | 'confirm'>(
    'initial'
  );
  const [confirmProps, setConfirmProps] = useState({
    open: false,
    description: '',
    reason: DeferralReason.Other,
  });
  const form = useForm<z.infer<typeof withdrawFormSchema>>({
    resolver: zodResolver(withdrawFormSchema),
    defaultValues: {
      description: '',
      termsAgreed: false,
      reason: '',
    },
  });

  const onCancelClick = () => {
    form.reset();
    onCancel();
  };

  return (
    <>
      {selectedState === 'initial' && (
        <div className="max-w-[620px]">
          <div className="px-0 sm:px-16">
            <div className="flex flex-col items-center text-center gap-2">
              <div
                className={`p-6 bg-destructive/20 rounded-full text-destructive mx-auto mb-2`}
              >
                <WithdrawIcon height="48" width="48" />
              </div>
              <h2
                className={`text-destructive font-semibold sm:text-2xl text-xl`}
              >
                You opted to withdraw from your ALX program
              </h2>
              <div className="text-sm">
                Withdrawing from your program will end it permanently. You will
                need to reapply.
              </div>
            </div>
            <div className="border-t-primary/20 border-b-primary/20 my-8 py-8 border-t border-b">
              <h3 className="font-semibold text-lg">
                Why do you want to withdraw from your program?
              </h3>
              <div className="opacity-70 font-light text-sm">
                We use this information for data purposes only and to improve
                the timing on our programs.
              </div>
              <Form {...form}>
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <Select
                      {...field}
                      onValueChange={(v) => {
                        field.onChange(v);
                        form.setValue('description', '');
                      }}
                      defaultValue="Select"
                    >
                      <SelectTrigger className="my-4">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {UpdateCourseReasons.map((reason) => (
                          <SelectItem value={reason}>
                            {capitalize(
                              reason.split('_').join(' ').toLowerCase()
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.watch('reason') === 'Other' && (
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Enter a description..."
                        />
                      </>
                    )}
                  />
                )}
                <div className="flex gap-2 mt-4">
                  <FormField
                    control={form.control}
                    name="termsAgreed"
                    render={({ field }) => (
                      <Checkbox
                        className="mt-0.5 border-border"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <label className="text-sm font-medium">
                    I understand that by withdrawing from my program, I am
                    permanently ending it and will need to reapply if I choose
                    to start again.
                  </label>
                </div>
              </Form>
            </div>
            <div className="flex gap-6 justify-center">
              <Button
                disabled={
                  !form.watch('reason') ||
                  (form.watch('reason') === DeferralReason.Other &&
                    !form.watch('description')) ||
                  !form.watch('termsAgreed')
                }
                variant="outlineDestructive"
                onClick={() => {
                  setConfirmProps({
                    open: true,
                    description: form.getValues('description'),
                    reason: ReasonsToEnum[form.getValues('reason')!],
                  });
                  setSelectedState('confirm');
                }}
              >
                Withdraw
              </Button>
              <Button variant="outline" onClick={onCancelClick}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {selectedState === 'confirm' && (
        <WithdrawProgramConfirmation
          programEnrollment={programEnrollment}
          afterConfirm={onCancelClick}
          onCancel={onCancelClick}
          {...confirmProps}
        />
      )}
    </>
  );
};
